import { useEffect, useState } from 'react';
import { useIdioma } from '../idioma';
import { CONSEJOS, TIPS, tipDeHoy } from './consejos';

/**
 * Igor, la mascota. Sale solo en momentos clave: bienvenida, esperas, celebraciones, errores, pantallas vacías y
 * consejos. Saluda, trabaja y celebra están animados (video con transparencia: HEVC para Safari, VP9 para los
 * demás); el resto son imágenes. Con "reducir movimiento" siempre es la imagen quieta.
 * Las poses salen de spikes/mascota-animar.mjs y spikes/salidas/mascota/.
 */
export type PoseDeIgor = 'saluda' | 'trabaja' | 'celebra' | 'consuela' | 'busca' | 'consejo' | 'camina' | 'sentado';
const ANIMADAS: PoseDeIgor[] = ['saluda', 'trabaja', 'celebra', 'camina'];

const sinMovimiento = () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Safari de verdad (no Chrome ni Firefox en iPhone, que también dicen «Safari» pero usan WebKit con HEVC igual). */
const ES_SAFARI = typeof navigator !== 'undefined' && /^((?!chrome|chromium|android|crios|fxios|edg).)*safari/i.test(navigator.userAgent) || (typeof navigator !== 'undefined' && /iphone|ipad|ipod/i.test(navigator.userAgent));

/**
 * Android no pinta la transparencia de los videos (06-10-2026: en el Inicio, ahora con la tarjeta morada, Igor salía
 * dentro de un recuadro blanco). Ahí va la imagen quieta, que sí es transparente.
 */
const SIN_ALFA = typeof navigator !== 'undefined' && /android/i.test(navigator.userAgent);

/** Sin `tam`, el tamaño lo pone la clase CSS. */
export function Igor({ pose, tam, clase = '' }: { pose: PoseDeIgor; tam?: number; clase?: string }) {
  const estilo = tam ? { width: tam, height: tam } : undefined;
  if (!ANIMADAS.includes(pose) || sinMovimiento() || SIN_ALFA) {
    return <img className={`igor ${clase}`} src={`/igor/${pose}.webp`} alt="" style={estilo} />;
  }
  return (
    // Una sola fuente según el navegador (30-09-2026): Chrome en Mac ya lee HEVC pero sin transparencia (Igor salía
    // en un cuadro negro) y Safari lee WebM pero sin transparencia. Safari → HEVC con alfa; los demás → VP9 con alfa.
    <video key={pose} className={`igor ${clase}`} style={estilo} autoPlay loop muted playsInline poster={`/igor/${pose}.webp`} aria-hidden="true"
      src={ES_SAFARI ? `/igor/${pose}-safari.mp4` : `/igor/${pose}.webm`} />
  );
}

/**
 * Un consejo de Igor para llenar el paso. Sale la primera vez; "Entendido" lo guarda en este navegador y deja solo
 * un botoncito con Igor para volver a verlo. Los textos son fijos (consejos.ts), sin IA.
 */
export function ConsejoDeIgor({ seccion }: { seccion: string }) {
  const { t } = useIdioma();
  const lista = CONSEJOS[seccion];
  const clave = `igor-consejo-${seccion}`;
  const [oculto, setOculto] = useState(() => { try { return localStorage.getItem(clave) === 'oculto'; } catch { return false; } });
  const [n, setN] = useState(0);
  if (!lista?.length) return null;
  const guardar = (v: boolean) => {
    setOculto(v);
    try { if (v) localStorage.setItem(clave, 'oculto'); else localStorage.removeItem(clave); } catch { /* sin almacenamiento, no pasa nada */ }
  };
  if (oculto) {
    return (
      <button className="consejo-mini" onClick={() => guardar(false)} aria-label={t('Ver un consejo de Igor')} title={t('Un consejo de Igor')}>
        <img src="/igor/consejo.webp" alt="" />
      </button>
    );
  }
  return (
    <aside className="consejo-igor" aria-label={t('Consejo de Igor')}>
      <img src="/igor/consejo.webp" alt="" />
      <div className="consejo-texto">
        <span className="consejo-tit">{t('Consejo de Igor')}</span>
        <p aria-live="polite">{t(lista[n % lista.length]!)}</p>
      </div>
      {lista.length > 1 && <button className="btn claro chico" onClick={() => setN(n + 1)}>{t('Otro consejo')}</button>}
      <button className="btn chico" onClick={() => guardar(true)}>{t('Entendido')}</button>
    </aside>
  );
}

/** Dónde se sentó Igor por última vez en cada sección, para que camine desde ahí al cambiar de paso. */
const donde = (seccion: string) => `igor-barra-${seccion}`;

/**
 * Igor en la barra de pasos: al llegar a un paso, aparece donde se sentó la última vez (el paso de antes, por
 * cualquier camino: aprobar, la barra, el menú), camina hasta este mirando hacia donde va y se sienta.
 */
export function IgorEnLaBarra({ seccion, indice, total }: { seccion: string; indice: number; total: number }) {
  const centro = (i: number) => `calc(${((i + 0.5) / total) * 100}% - 28px)`;
  // Se guarda {desde, hacia, cuando}. Si la pantalla se vuelve a montar a mitad de camino (carga de datos), la
  // caminata sigue desde el mismo sitio en vez de aparecer sentado.
  const [desde] = useState(() => {
    if (sinMovimiento()) return indice;
    try {
      const g = JSON.parse(sessionStorage.getItem(donde(seccion)) ?? 'null') as { desde: number; hacia: number; cuando: number } | null;
      if (!g) return indice;
      const d = g.hacia === indice ? (Date.now() - g.cuando < 2500 ? g.desde : indice) : g.hacia;
      return d >= 0 && d < total ? d : indice;
    } catch {
      return indice;
    }
  });
  const [llego, setLlego] = useState(desde === indice);
  const [caminando, setCaminando] = useState(desde !== indice);
  useEffect(() => {
    if (llego) return;
    // Un instante pintado en el paso de antes y arranca.
    const id = window.setTimeout(() => setLlego(true), 150);
    return () => clearTimeout(id);
  }, [llego]);
  useEffect(() => {
    try { sessionStorage.setItem(donde(seccion), JSON.stringify({ desde, hacia: indice, cuando: Date.now() })); } catch { /* nada */ }
  }, [seccion, desde, indice]);
  const pasos = Math.abs(indice - desde);
  return (
    <div className="igor-barra" aria-hidden="true">
      <div
        className={indice < desde && caminando ? 'igor-barra-igor al-reves' : 'igor-barra-igor'}
        style={{ left: centro(llego ? indice : desde), transitionDuration: `${Math.max(pasos, 1) * 1.3}s` }}
        onTransitionEnd={() => setCaminando(false)}
      >
        <Igor pose={caminando ? 'camina' : 'sentado'} tam={56} />
      </div>
    </div>
  );
}

/**
 * Un consejo de Igor mientras se espera (03-11-2026, pedido del dueño): los consejos del día, que cambian solos cada
 * 20 segundos; con «Otro» se pasa antes. Empieza por el de hoy.
 */
export function TipMientras() {
  const { t } = useIdioma();
  const [n, setN] = useState(tipDeHoy);
  useEffect(() => {
    const i = setInterval(() => setN((x) => x + 1), 20_000);
    return () => clearInterval(i);
  }, [n]);
  const tip = TIPS[n % TIPS.length]!;
  return (
    <aside className="consejo-igor tip-mientras" aria-label={t('Consejo de Igor')}>
      <img src="/igor/consejo.webp" alt="" />
      <div className="consejo-texto">
        <span className="consejo-tit">{t('Mientras tanto, un consejo')}</span>
        <p aria-live="polite"><b>{t(tip.titulo)}.</b> {t(tip.texto)}</p>
      </div>
      <button className="btn claro chico" onClick={() => setN(n + 1)}>{t('Otro consejo')}</button>
    </aside>
  );
}
